import { request, getBaseUrl, sessionFetch } from './http';

export async function getCustomFormLookups() {
    return request('/custom-forms/lookups');
  }

export async function getCustomFormSites(companyId) {
    return request(`/custom-forms/sites?companyId=${encodeURIComponent(companyId)}`);
  }

export async function getCustomFormReferenceOptions(table, companyId) {
    const params = new URLSearchParams();
    params.set('table', table);
    if (companyId != null && companyId !== '') params.set('companyId', companyId);
    return request(`/custom-forms/reference-options?${params.toString()}`);
  }

export async function getCustomFormUserOptions({ companyId, source, siteView, roles, siteId, labelMode } = {}) {
    const params = new URLSearchParams();
    if (companyId != null && companyId !== '') params.set('companyId', companyId);
    if (source) params.set('source', source);
    if (siteView) params.set('siteView', siteView);
    if (siteId != null && siteId !== '') params.set('siteId', siteId);
    if (Array.isArray(roles) && roles.length) params.set('roles', roles.join(','));
    if (labelMode) params.set('labelMode', labelMode);
    return request(`/custom-forms/user-options?${params.toString()}`);
  }

export async function getCustomForms({ companyId, published = false } = {}) {
    const params = new URLSearchParams();
    if (companyId != null && companyId !== '') params.set('companyId', companyId);
    if (published) params.set('published', '1');
    const query = params.toString();
    return request(`/custom-forms${query ? `?${query}` : ''}`);
  }

export async function getCustomForm(id) {
    return request(`/custom-forms/${encodeURIComponent(id)}`);
  }

export async function createCustomForm(payload) {
    return request('/custom-forms', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

export async function updateCustomForm(id, payload) {
    return request(`/custom-forms/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

export async function deleteCustomForm(id) {
    return request(`/custom-forms/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

export async function saveCustomFormAsTemplate(id, payload = {}) {
    return request(`/custom-forms/${encodeURIComponent(id)}/template`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

export async function createCustomFormFromTemplate(payload) {
    return request('/custom-forms/from-template', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

export async function getCustomReportCatalog() {
    return request('/custom-reports/catalog');
  }

export async function getCustomReportLookups() {
    return request('/custom-reports/lookups');
  }

export async function getCustomReportSites(companyId) {
    const params = new URLSearchParams();
    if (companyId != null && companyId !== '') params.set('companyId', companyId);
    const query = params.toString();
    return request(`/custom-reports/sites${query ? `?${query}` : ''}`);
  }

export async function getCustomReports({ published = false } = {}) {
    const params = new URLSearchParams();
    if (published) params.set('published', '1');
    const query = params.toString();
    return request(`/custom-reports${query ? `?${query}` : ''}`);
  }

export async function getCustomReport(id) {
    return request(`/custom-reports/${encodeURIComponent(id)}`);
  }

export async function createCustomReport(payload) {
    return request('/custom-reports', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

export async function updateCustomReport(id, payload) {
    return request(`/custom-reports/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

export async function deleteCustomReport(id) {
    return request(`/custom-reports/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

export async function queryCustomReport(id, filters) {
    return request(`/custom-reports/${encodeURIComponent(id)}/query`, {
      method: 'POST',
      body: JSON.stringify({ filters })
    });
  }

export async function previewCustomReportQuery(payload) {
    return request('/custom-reports/preview-query', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

export async function getCustomFormRecords(formId) {
    return request(`/custom-forms/${encodeURIComponent(formId)}/records`);
  }

export async function createCustomFormRecord(formId, payload) {
    return request(`/custom-forms/${encodeURIComponent(formId)}/records`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

export async function getCustomFormRecord(id) {
    return request(`/custom-forms/records/${encodeURIComponent(id)}`);
  }

export async function updateCustomFormRecord(id, payload) {
    return request(`/custom-forms/records/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

export async function deleteCustomFormRecord(id) {
    return request(`/custom-forms/records/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

export async function previewCustomFormPdf(payload) {
    const url = `${String(getBaseUrl() || '').replace(/\/+$/, '')}/api/custom-forms/preview-pdf`;
    const response = await sessionFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload || {})
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.message || `Failed to generate PDF (${response.status})`);
    }
    const blob = await response.blob();
    if (!blob.size) throw new Error('Empty PDF response');
    return blob;
  }

export async function downloadCustomFormRecordPdf(recordId, templateKey) {
    const query = templateKey ? `?template=${encodeURIComponent(templateKey)}` : '';
    const url = `${String(getBaseUrl() || '').replace(/\/+$/, '')}/api/custom-forms/records/${encodeURIComponent(recordId)}/pdf${query}`;
    const response = await sessionFetch(url, {
      method: 'POST',
      headers: {}
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.message || `Failed to download PDF (${response.status})`);
    }
    const blob = await response.blob();
    if (!blob.size) throw new Error('Empty PDF response');
    return blob;
  }

export const customFormsApi = {
  getCustomFormLookups,
  getCustomFormSites,
  getCustomFormReferenceOptions,
  getCustomFormUserOptions,
  getCustomForms,
  getCustomForm,
  createCustomForm,
  updateCustomForm,
  deleteCustomForm,
  saveCustomFormAsTemplate,
  createCustomFormFromTemplate,
  getCustomReportCatalog,
  getCustomReportLookups,
  getCustomReportSites,
  getCustomReports,
  getCustomReport,
  createCustomReport,
  updateCustomReport,
  deleteCustomReport,
  queryCustomReport,
  previewCustomReportQuery,
  getCustomFormRecords,
  createCustomFormRecord,
  getCustomFormRecord,
  updateCustomFormRecord,
  deleteCustomFormRecord,
  previewCustomFormPdf,
  downloadCustomFormRecordPdf,
};

